// Pick a catalogue product to put on an order.
//
// Used by the Create-order modal (lines are held locally until the order
// is saved) and by the order-detail modal's Add product button (the line
// is POSTed straight away). Search is the same exact-match lookup the
// SKU-mapping modal uses; choosing a result reveals a quantity + unit
// price strip. Unit price starts at 0.00 (Alex, 2026-09-15) and is typed
// in by the admin.
//
// Stock: with a `warehouse` the search also returns each hit's free
// stock there (on hand minus what live orders have already claimed), and
// the quantity is capped at it — the warehouse is never handed a line it
// cannot pick. `committedQty` is what the caller has already put on this
// order for a SKU (draft lines, or a held order's existing line), which
// comes off the cap as well. The server re-checks on save.
import { useState } from "react";
import { X, Search, Package, AlertCircle, Plus } from "lucide-react";
import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";
import { useReadOnly } from "../../access/ReadOnly";
import FlowLoader from "../FlowLoader";

type SearchField = "sku" | "ean" | "title";

export type PickedProduct = {
    sku: string;
    title: string | null;
    barcode: string | null;
    image_url: string | null;
    weight_grams: number | null;
    cost_price: number | string | null;
    // Present only when the search was warehouse-scoped.
    free_stock?: number | null;
};

type Props = {
    // Optional context line under the title ("Order #MAN12").
    subtitle?: string;
    // Warehouse the order ships from — enables the free-stock figure/cap.
    warehouse?: string;
    // Units of a SKU the caller already has on this order (not yet
    // counted in the warehouse's reserved demand). Subtracted from the cap.
    committedQty?: (sku: string) => number;
    onPick: (product: PickedProduct, quantity: number, unitPrice: number) => void | Promise<void>;
    onClose: () => void;
    // True while the parent is saving the line — disables the Add button.
    busy?: boolean;
};

// "4.5000" / 4.5 / null → 4.5 / null
export const numberOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

// How many more units of a hit may go on the order: its free stock less
// what the order already carries. null = unknown (no warehouse given).
export const addableQty = (p: PickedProduct, committed: number): number | null => {
    const free = numberOrNull(p.free_stock);
    if (free === null) return null;
    return Math.max(0, Math.floor(free) - committed);
};

const OrderProductPicker = ({ subtitle, warehouse, committedQty, onPick, onClose, busy = false }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [field, setField] = useState<SearchField>("sku");
    const [q, setQ] = useState("");
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<PickedProduct[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [chosen, setChosen] = useState<PickedProduct | null>(null);
    const [quantity, setQuantity] = useState("1");
    const [unitPrice, setUnitPrice] = useState("");

    const capOf = (p: PickedProduct) => addableQty(p, committedQty?.(p.sku) ?? 0);

    const handleSearch = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!q.trim()) return;
        setSearching(true);
        setError(null);
        setResults(null);
        setChosen(null);
        try {
            const params = new URLSearchParams({ field, q: q.trim() });
            if (warehouse) params.set("warehouse", warehouse);
            const res = await apiFetch(`/products/exact-search?${params.toString()}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const hits: PickedProduct[] = Array.isArray(data.products) ? data.products : [];
            setResults(hits);
            // One hit is the overwhelmingly common case for an exact SKU /
            // barcode search — skip the extra click.
            if (hits.length === 1) choose(hits[0]);
        } catch {
            setError(t("sku_map_search_failed"));
        } finally {
            setSearching(false);
        }
    };

    const choose = (p: PickedProduct) => {
        setChosen(p);
        setQuantity("1");
        setUnitPrice("0.00");
    };

    const cap = chosen ? capOf(chosen) : null;
    const qtyNum = Number(quantity);
    const priceNum = Number(unitPrice);
    const overCap = cap !== null && qtyNum > cap;
    const canAdd = !!chosen && Number.isInteger(qtyNum) && qtyNum >= 1 && !overCap
        && unitPrice.trim() !== "" && Number.isFinite(priceNum) && priceNum >= 0 && !busy && !readOnly;

    const submit = async () => {
        if (readOnly || !chosen || !canAdd) return;
        await onPick(chosen, qtyNum, Math.round(priceNum * 100) / 100);
    };

    // Stock badge for a hit: "Free stock: n" or "No free stock".
    const stockBadge = (p: PickedProduct) => {
        const n = capOf(p);
        if (n === null) return null;
        return (
            <span className={`order-line-stock${n <= 0 ? " order-line-stock--none" : ""}`}>
                {n <= 0 ? t("stock_none_free") : t("stock_free_n", { n: String(n) })}
            </span>
        );
    };

    // stopPropagation: this picker opens on top of another modal (the
    // create-order form) whose overlay also closes on click — a backdrop
    // click here must close only the picker.
    return (
        <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
            <div
                className="modal-card sku-map-modal"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: 640 }}
            >
                <div className="modal-card-header">
                    <div>
                        <h2>{t("add_product_title")}</h2>
                        {subtitle && <p className="sku-map-context-title" style={{ margin: 0 }}>{subtitle}</p>}
                    </div>
                    <button className="modal-close" onClick={onClose} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <form className="sku-map-search" onSubmit={handleSearch}>
                        <div className="sku-map-field-toggle" role="radiogroup" aria-label={t("sku_map_search_by")}>
                            {(["sku", "ean", "title"] as SearchField[]).map((f) => (
                                <button
                                    key={f}
                                    type="button"
                                    role="radio"
                                    aria-checked={field === f}
                                    className={`sku-map-field-btn${field === f ? " sku-map-field-btn--active" : ""}`}
                                    onClick={() => { setField(f); setResults(null); setChosen(null); }}
                                >
                                    {t(`sku_map_field_${f}`)}
                                </button>
                            ))}
                        </div>
                        <div className="sku-map-input-row">
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={t(`sku_map_placeholder_${field}`)}
                                className="sku-map-input"
                                aria-label={t("search")}
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={!q.trim() || searching}
                            >
                                <Search size={16} />
                                {t("search")}
                            </button>
                        </div>
                    </form>

                    {error && (
                        <div className="dispatch-error-banner" role="alert">
                            <AlertCircle size={18} />
                            <span>{error}</span>
                        </div>
                    )}

                    {searching && (
                        <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
                            <FlowLoader message={t("loading")} />
                        </div>
                    )}

                    {results !== null && !searching && (
                        <div className="sku-map-results">
                            {results.length === 0 ? (
                                <p className="sku-map-empty">{t("sku_map_no_results")}</p>
                            ) : (
                                results.map((p) => (
                                    <button
                                        key={p.sku}
                                        type="button"
                                        className={`sku-map-result${chosen?.sku === p.sku ? " sku-map-result--chosen" : ""}`}
                                        aria-pressed={chosen?.sku === p.sku}
                                        onClick={() => choose(p)}
                                    >
                                        {p.image_url ? (
                                            <img src={p.image_url} alt="" className="sku-map-result-img" />
                                        ) : (
                                            <div className="sku-map-result-img sku-map-result-img--ph">
                                                <Package size={16} />
                                            </div>
                                        )}
                                        <div className="sku-map-result-info">
                                            <p className="sku-map-result-title">{p.title || p.sku}</p>
                                            <p className="sku-map-result-meta">
                                                <span>{p.sku}</span>
                                                {p.barcode && <span> · {p.barcode}</span>}
                                                {stockBadge(p)}
                                            </p>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}

                    {chosen && (
                        <div className="order-line-picker-strip">
                            <div className="order-line-picker-field">
                                <label className="dispatch-edit-label" htmlFor="order-line-qty">{t("qty")}</label>
                                <input
                                    id="order-line-qty"
                                    type="number"
                                    min={1}
                                    max={cap ?? undefined}
                                    step={1}
                                    inputMode="numeric"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    aria-invalid={overCap || undefined}
                                    title={overCap ? t("stock_only_n_free", { n: String(cap) }) : undefined}
                                />
                            </div>
                            <div className="order-line-picker-field">
                                <label className="dispatch-edit-label" htmlFor="order-line-price">{t("unit_price")}</label>
                                <input
                                    id="order-line-price"
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    inputMode="decimal"
                                    value={unitPrice}
                                    onChange={(e) => setUnitPrice(e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-success"
                                disabled={!canAdd}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : (overCap && cap !== null ? t("stock_only_n_free", { n: String(cap) }) : undefined)}
                                onClick={() => { void submit(); }}
                            >
                                <Plus size={14} />
                                {busy ? t("submitting") : t("add")}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderProductPicker;
